/**
 * "Datenqualität & Hinweise" — what the numbers above cannot show on their own.
 *
 * FOUR POPULATIONS, FOUR GROUPS, NEVER ONE TOTAL.
 * `data_quality` nests four finding sets that describe DIFFERENT populations:
 * contract tenancies, all mirror invoices, open receivables, and paid invoices.
 * The same invoice can legitimately appear in three of them for three different
 * reasons. Adding the counts would double-count by construction and produce a
 * confident "12 Probleme" that means nothing, so each group carries its own
 * number and no cross-group sum exists anywhere in this file.
 *
 * AMOUNTS ARE ONLY SHOWN WHERE THE BACKEND NAMES ONE **IN FRANCS**.
 * Several findings carry a named total, and they fall into two kinds. The six
 * that sit AFTER their service's currency gate — `status_amount_mismatch_open_amount`,
 * `credit_balances_total`, `payment_sum_mismatch_amount`, `canceled_invoice_amount`,
 * `no_payment_date_amount`, `negative_payment_amount` — are francs by construction
 * and are shown as money.
 *
 * The other three are NOT. `non_chf_amount` adds every verified foreign invoice
 * into one scalar with no code beside it, `unknown_currency_amount` sums invoices
 * whose currency the provider never identified, and `orphan_payment_amount` sums
 * payment rows with no invoice and therefore no currency at all. The backend marks
 * them by omission: none is added to `excluded_chf_amount_total`, whose own
 * docstring gives the reason — "100 CHF + 100 EUR is 200 of nothing". Printing any
 * of them as CHF would state something the data cannot support, so they show their
 * COUNT and a note instead of a figure.
 *
 * Where no named total exists at all, none is displayed either — reconstructing one
 * by summing a list of refs would be this component inventing a money figure.
 *
 * This replaces the design reference's "Erkenntnisse" card. Every phrasing there
 * ("Zahlungseingänge liegen über Vorjahresniveau", "Mietausfallquote: gut")
 * needs a threshold nobody has set, and one of them needs a prior-year baseline
 * the contract explicitly cannot provide. Counts are the honest version.
 */
import React, { useCallback, useState } from "react";
import { AlertTriangle, ChevronRight, CircleAlert, Info } from "lucide-react";

import RevenueQualityDetailDrawer from "./RevenueQualityDetailDrawer";
import { fmtInvoiceMoney, fmtMoney } from "./revenueAnalyticsFormat";
import {
  PROVIDER_MISSING_DETAIL_KEY,
  PROVIDER_MISSING_KEY,
  useProviderMissingInvoices,
} from "./useProviderMissingInvoices";

/**
 * Said for every currency finding, in all three groups.
 *
 * The same sentence for the same fact: a verified foreign amount and an
 * unverified one are both kept out of every CHF sum, for different reasons that
 * must stay distinguishable.
 */
const NON_CHF_DESCRIPTION =
  "Verifizierte Fremdwährung. Diese Beträge werden nie in CHF summiert und nie umgerechnet — es existiert kein Wechselkurs in diesem System.";

const UNKNOWN_CURRENCY_DESCRIPTION =
  "Der Provider hat die Währung nie bestätigt (Migration 159). Das ist ein dritter Zustand: nicht CHF, nicht fremd, sondern unbekannt — und in keiner CHF-Summe enthalten.";

/**
 * The findings, as data.
 *
 * `key` is the response's own field name in every case, so no row here can
 * display a category the backend does not publish. `kind: "count"` marks the
 * fields that are already integers rather than ref lists. `amountKey` names the
 * backend's own CHF total for that finding; `denomination` marks the findings whose
 * aggregate has no single currency behind it and therefore shows a note instead of
 * a figure. The two are mutually exclusive — a value is either francs or it is not.
 */
export const DATA_QUALITY_GROUPS = [
  {
    id: "expected",
    title: "Vertrags- und Solldaten",
    population: "Mietverhältnisse mit Revenue-Zeilen im gewählten Monat",
    items: [
      {
        key: "tenancies_without_rent_revenue",
        label: "Mietverhältnisse ohne Miet-Revenue-Zeile",
        severity: "warn",
        detailType: "tenancy",
      },
      {
        key: "tenancies_without_unit",
        label: "Mietverhältnisse ohne Einheit",
        severity: "warn",
        detailType: "tenancy",
      },
      {
        key: "multiple_rent_rows",
        label: "Mehrere Miet-Zeilen je Mietverhältnis",
        severity: "warn",
        detailType: "tenancy",
      },
      {
        key: "overlapping_recurring_rows",
        label: "Überlappende wiederkehrende Zeilen",
        severity: "bad",
        detailType: "overlap",
      },
      {
        key: "frequency_not_monetized",
        label: "Frequenz nicht monetarisierbar",
        severity: "warn",
        detailType: "revenueRow",
        description:
          "Die Frequenz liegt ausserhalb von monatlich / jährlich / einmalig. Der kanonische Helper monetarisiert sie mit 0 — es wird keine Wochen- oder Quartalsformel erfunden.",
      },
      { key: "negative_amount", label: "Negative Beträge", severity: "warn", detailType: "revenueRow" },
      {
        key: "revenue_row_outside_tenancy_window",
        label: "Revenue-Zeile ausserhalb der Laufzeit",
        severity: "bad",
        detailType: "revenueRow",
      },
      {
        key: "one_time_end_date_ignored",
        label: "Einmalzeile mit ignoriertem Enddatum",
        severity: "info",
        detailType: "revenueRow",
        description:
          "Das Enddatum wird von der kanonischen Semantik ignoriert. Der Betrag bleibt unverändert; nur das ignorierte Feld wird hier sichtbar gemacht.",
      },
      {
        key: "one_time_without_month",
        label: "Einmalzeile ohne zuordenbaren Monat",
        severity: "warn",
        detailType: "revenueRow",
      },
      {
        key: "suspicious_non_revenue_type",
        label: "Typ wirkt wie Kaution statt Ertrag",
        severity: "info",
        detailType: "revenueRow",
        description:
          "Nur gemeldet: Kautionen leben auf Tenancy-Spalten, aber `type` hat keinen DB-CHECK. Es wird keine neue Ausschlussregel erfunden.",
      },
      {
        key: "zero_amount_count",
        label: "Zeilen mit Betrag null",
        severity: "info",
        kind: "count",
      },
    ],
  },
  {
    id: "attribution",
    title: "Periodenzuordnung",
    population: "Alle gespiegelten Bexio-Rechnungen",
    items: [
      {
        key: "duplicate_bexio_invoice_ids",
        label: "Mehrfach beanspruchte Rechnungen",
        severity: "bad",
        detailType: "duplicateGroup",
        description:
          "Die Rechnung wird in KEINER Periode gezählt — nicht einmal, nicht zweimal. Welcher der beanspruchenden Einträge korrekt ist, entscheidet dieses System nicht.",
      },
      {
        key: "ledger_without_mirror",
        label: "Ledger-Eintrag ohne Spiegel-Rechnung",
        severity: "warn",
        detailType: "ledger",
        description:
          "Der Ledger beweist einen Mietmonat für eine Rechnung, die der Spiegel nicht hält — Sync-Lücke oder beim Provider gelöscht. Es werden keine Beträge gebucht.",
      },
      {
        key: "tenancy_mismatch",
        label: "Mietverhältnis stimmt nicht überein",
        severity: "warn",
        detailType: "ledger",
        description:
          "Die manuelle Mapping-Zuordnung der Rechnung weicht vom Ledger ab. Gebucht wird immer die Ledger-Zuordnung; hier wird nur berichtet.",
      },
      {
        key: "non_success_with_bexio_id",
        label: "Nicht erfolgreicher Eintrag mit Bexio-ID",
        severity: "warn",
        detailType: "ledger",
        description:
          "Der Eintrag zeigt auf ein echtes Bexio-Objekt und behauptet gleichzeitig, die Erstellung sei nicht abgeschlossen. Kein DB-Constraint verbindet die beiden Felder.",
      },
      {
        key: "created_without_bexio_id",
        label: "Erstellt ohne Bexio-ID",
        severity: "warn",
        detailType: "ledger",
        description: "Das Spiegelbild des Falls darüber — dieselbe verletzte Invariante.",
      },
      {
        key: "non_chf",
        label: "Fremdwährung (verifiziert)",
        severity: "warn",
        detailType: "attributionInvoice",
        description: NON_CHF_DESCRIPTION,
      },
      {
        key: "unknown_currency",
        label: "Währung nicht verifiziert",
        severity: "warn",
        detailType: "attributionInvoice",
        description: UNKNOWN_CURRENCY_DESCRIPTION,
      },
    ],
  },
  {
    id: "receivables",
    title: "Offene Forderungen",
    population: "Offene Posten des Rechnungsspiegels",
    items: [
      {
        key: "status_amount_mismatch",
        label: "Status und Betrag widersprechen sich",
        severity: "bad",
        amountKey: "status_amount_mismatch_open_amount",
        detailType: "agingInvoice",
        description:
          "Bexio' eigener Status widerspricht dem offenen Betrag. Die positiven Reste sind als Summe ausgewiesen, damit kein offener Franken unsichtbar bleibt.",
      },
      {
        key: "credit_balances",
        label: "Guthaben / Überzahlungen",
        severity: "warn",
        amountKey: "credit_balances_total",
        detailType: "agingInvoice",
        description: "Negative offene Beträge, vorzeichenrichtig und nie saldiert.",
      },
      {
        key: "canceled_with_open_amount",
        label: "Storniert mit offenem Betrag",
        severity: "bad",
        detailType: "agingInvoice",
      },
      {
        key: "unknown_status",
        label: "Unbekannter Rechnungsstatus",
        severity: "warn",
        detailType: "agingInvoice",
      },
      {
        key: "non_chf",
        label: "Fremdwährung (verifiziert)",
        severity: "warn",
        detailType: "agingInvoice",
        description: NON_CHF_DESCRIPTION,
      },
      {
        key: "unknown_currency",
        label: "Währung nicht verifiziert",
        severity: "warn",
        detailType: "agingInvoice",
        description: UNKNOWN_CURRENCY_DESCRIPTION,
      },
      {
        key: "missing_due_date_count",
        label: "Offene Posten ohne Fälligkeitsdatum",
        severity: "warn",
        kind: "count",
      },
      // The one finding whose count does NOT live under `data_quality`.
      //
      // It is published on `current_receivables.excluded.provider_missing` — the
      // financial block itself — because it is the reason the receivables total is
      // shorter than the mirror, and it is resolved in the aging service BEFORE
      // status and before currency. `countFrom` says where to read it; nothing
      // here re-derives it.
      //
      // Deliberately NOT sourced from period attribution, which publishes
      // provider-missing numbers of its own. Those describe invoices a draft-ledger
      // row points at, and the six real production cases had `draft_requests = 0` —
      // an attribution-based category would have shown zero for exactly the
      // situation it exists to surface.
      {
        key: PROVIDER_MISSING_KEY,
        label: "In Vantio vorhanden, in Bexio nicht mehr vorhanden",
        // The same `warn` every other exclusion here uses. The invoices are not
        // broken and nothing failed; a completed inventory simply no longer finds
        // them, and `bad` would call that an error.
        severity: "warn",
        kind: "count",
        countFrom: "current_receivables.excluded",
        detailType: "providerMissingInvoice",
        // The records are fetched, paginated, when this finding is opened — see
        // `useProviderMissingInvoices`. There is no array for them on the wire.
        remote: true,
        description:
          "Diese Rechnungen bleiben im lokalen Bexio-Verlauf gespeichert, wurden bei der " +
          "vollständigen Bexio-Prüfung jedoch nicht mehr gefunden und werden deshalb nicht " +
          "als aktuelle Forderung berücksichtigt. Die angezeigten Beträge sind die zuletzt " +
          "gespiegelten Werte, keine offene Forderung.",
      },
    ],
  },
  {
    id: "cashflow",
    title: "Zahlungseingänge",
    population: "Gespiegelte Zahlungszeilen",
    items: [
      {
        key: "payment_sum_mismatch",
        label: "Zahlungssumme weicht von total_paid ab",
        severity: "bad",
        amountKey: "payment_sum_mismatch_amount",
        detailType: "cashflowInvoice",
        description:
          "Die Differenz kommt aus dem Backend und wird hier nicht nachgerechnet.",
      },
      {
        key: "canceled_invoice",
        label: "Zahlungen auf stornierter Rechnung",
        severity: "bad",
        amountKey: "canceled_invoice_amount",
        detailType: "cashflowInvoice",
      },
      // No invoice, therefore no currency: a payment row carries none of its own
      // (migration 139:301-311), so `orphan_payment_amount` is a bare number.
      {
        key: "orphan_payment",
        label: "Zahlung ohne Rechnung",
        severity: "warn",
        denomination: "unknown",
        detailType: "cashflowPayment",
        description:
          "Ohne Rechnung gibt es keine Währung: eine Zahlungszeile trägt keine eigene. Die Beträge stehen deshalb ohne Währungsangabe.",
      },
      {
        key: "no_payment_date",
        label: "Zahlung ohne Datum",
        severity: "warn",
        amountKey: "no_payment_date_amount",
        detailType: "cashflowPayment",
        description:
          "Ohne Datum lässt sich kein Monat bestimmen; die Zeile wird auf keine Monatsachse gebucht.",
      },
      {
        key: "negative_payment",
        label: "Negative Zahlung",
        severity: "warn",
        amountKey: "negative_payment_amount",
        detailType: "cashflowPayment",
      },
      // `non_chf_amount` sums EVERY verified foreign invoice into one scalar with no
      // code beside it — a EUR and a USD invoice land in the same number, so it is a
      // total of nothing in particular.
      {
        key: "non_chf",
        label: "Fremdwährung (verifiziert)",
        severity: "warn",
        denomination: "mixed",
        detailType: "cashflowInvoice",
        description: NON_CHF_DESCRIPTION,
      },
      {
        key: "unknown_currency",
        label: "Währung nicht verifiziert",
        severity: "warn",
        denomination: "unknown",
        detailType: "cashflowInvoice",
        description: UNKNOWN_CURRENCY_DESCRIPTION,
      },
      {
        key: "paid_without_payment_rows",
        label: "Bezahlt ohne Zahlungszeilen",
        severity: "warn",
        detailType: "cashflowInvoice",
        description:
          "Als bezahlt markiert, aber ohne Zahlungszeilen: es existiert kein Datum, also kann kein Monat abgeleitet werden. Aus total_paid wird nie ein Monat gebucht.",
      },
      {
        key: "status_mismatch_counted",
        label: "Trotz abweichendem Status gezählt",
        severity: "info",
        detailType: "cashflowInvoice",
        description:
          "Eine Warnung, kein Ausschluss: diese Zeilen WURDEN gezählt, weil eine bestätigte Zahlung eine echte Geldbewegung ist.",
      },
    ],
  },
];

/**
 * Which block of the response holds a finding's value.
 *
 * Almost every finding reads `data_quality.<group>`. One reads
 * `current_receivables.excluded`, because that is where the backend publishes it —
 * it is a property of the receivables figure, not a separate quality report. A
 * marker on the item rather than a special case in every consumer, so the count in
 * the summary card, the count in the group badge and the count on the row are one
 * lookup and cannot drift apart.
 */
function findingSource(dataQuality, group, item, receivables) {
  if (item.countFrom === "current_receivables.excluded") return receivables?.excluded;
  return dataQuality?.[group.id];
}

/** How many findings one group holds. Never summed with another group's. */
export function groupFindingCount(dataQuality, group, receivables) {
  let n = 0;
  group.items.forEach((item) => {
    const src = findingSource(dataQuality, group, item, receivables);
    if (!src) return;
    const v = src[item.key];
    if (item.kind === "count") {
      if (Number(v ?? 0) > 0) n += 1;
    } else if (Array.isArray(v) && v.length > 0) {
      n += 1;
    }
  });
  return n;
}

/** True when ANY group has something to say. Used by the warning strip. */
export function hasAnyFindings(dataQuality, receivables) {
  return DATA_QUALITY_GROUPS.some((g) => groupFindingCount(dataQuality, g, receivables) > 0);
}

/**
 * What is said INSTEAD of an amount when the backend's aggregate has no single
 * denomination behind it.
 *
 * Three findings are in that position and none of them is a franc total:
 * `non_chf_amount` adds every verified foreign invoice into one scalar without a
 * code beside it, `unknown_currency_amount` sums invoices whose currency the
 * provider never identified, and `orphan_payment_amount` sums payment rows that
 * have no invoice and therefore no currency at all. The backend says so in the
 * field itself — none of the three is added to `excluded_chf_amount_total`,
 * because "100 CHF + 100 EUR is 200 of nothing".
 *
 * The four cashflow amounts that DO carry `amountKey` sit after the currency gate
 * and are francs by construction, as do both receivables amounts; those keep
 * their figure unchanged.
 */
const DENOMINATION_NOTE = {
  mixed: "Keine CHF-Gesamtsumme verfügbar",
  unknown: "Nicht als CHF aggregierbar",
};

const SEVERITY = {
  bad: {
    icon: CircleAlert,
    className: "text-red-700 dark:text-red-400",
    word: "Fehler",
  },
  warn: {
    icon: AlertTriangle,
    className: "text-amber-700 dark:text-amber-300",
    word: "Warnung",
  },
  info: { icon: Info, className: "text-muted-foreground", word: "Hinweis" },
};

/**
 * One finding — a button where records exist behind it, static text where none do.
 *
 * INTERACTIVITY FOLLOWS THE DATA, NOT THE CONFIG.
 * A row opens a drawer only when its finding names a ref family AND the array is
 * actually populated. Two findings (`zero_amount_count`,
 * `missing_due_date_count`) are integers with no item list on the wire at all, and
 * they stay static with a line saying so: a chevron that opens an empty drawer
 * promises detail the response does not carry, which is worse than no chevron.
 */
function FindingRow({ item, source, group, onOpenDetail }) {
  const value = source?.[item.key];
  const count = item.kind === "count" ? Number(value ?? 0) : Array.isArray(value) ? value.length : 0;
  if (count <= 0) return null;

  const sev = SEVERITY[item.severity] || SEVERITY.info;
  const Icon = sev.icon;
  const amount = item.amountKey ? source?.[item.amountKey] : undefined;
  const note = item.denomination ? DENOMINATION_NOTE[item.denomination] : undefined;

  const items = Array.isArray(value) ? value : [];
  // A remote finding carries no array on the wire BY DESIGN: its records are read
  // separately and paginated, so openability follows the count, not an array that
  // was never sent. Every other finding still needs real records behind it — a
  // chevron onto an empty drawer promises detail the response does not carry.
  const canOpen = Boolean(item.detailType) && (item.remote === true || items.length > 0);

  const content = (
    <>
      {/* Severity is carried by an icon and a word, not by colour alone. */}
      <Icon aria-hidden="true" className={`mt-0.5 h-4 w-4 shrink-0 ${sev.className}`} />
      <span className="min-w-0 flex-1 text-sm text-foreground">
        {item.label}
        <span className="sr-only"> ({sev.word})</span>
        {amount != null ? (
          <span className="block text-xs text-muted-foreground">Betrag: {fmtMoney(amount)}</span>
        ) : null}
        {/* No figure at all, deliberately. The backend DOES return a number for
            these, but it is a sum across denominations that were never the same —
            printing it in any currency, or bare next to figures that are francs,
            would state something the data cannot support. The count above is the
            part that is true. */}
        {note ? <span className="block text-xs text-muted-foreground">{note}</span> : null}
        {!canOpen ? (
          <span className="block text-xs text-muted-foreground">
            Keine Einzeldetails verfügbar
          </span>
        ) : null}
      </span>
      <span className="shrink-0 rounded-full border border-border bg-muted px-2 py-0.5 text-xs font-medium text-foreground">
        {count}
      </span>
    </>
  );

  if (!canOpen) {
    return (
      <li className="flex items-start gap-2 border-b border-border/60 py-1.5 last:border-b-0">
        {content}
      </li>
    );
  }

  return (
    <li className="border-b border-border/60 last:border-b-0">
      {/* A real button, not a clickable row: this opens a dialog. The accessible
          name carries the finding AND its count, because "3" beside "2" tells a
          screen reader nothing on its own. */}
      <button
        type="button"
        onClick={() =>
          onOpenDetail({
            key: `${group.id}.${item.key}`,
            label: item.label,
            // Which population this finding describes. Three groups carry a
            // "Währung nicht verifiziert" over three different sets, so the
            // group is not decoration: without it the drawer title would be
            // ambiguous and two buttons on the page would be indistinguishable.
            context: group.population,
            description: item.description,
            family: item.detailType,
            // Whether the drawer reads `items` or the state the section hands it.
            remote: item.remote === true,
            // The count the reader just saw on this row. For a remote finding it is
            // the analytics figure and stays the header's authority, so opening the
            // drawer cannot quietly restate the number with the list's own total.
            count,
            items,
          })
        }
        aria-label={`${group.title}: ${item.label}, ${count} ${
          count === 1 ? "Eintrag" : "Einträge"
        } anzeigen`}
        className="flex w-full items-start gap-2 rounded py-1.5 text-left hover:bg-muted/50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
      >
        {content}
        <ChevronRight
          aria-hidden="true"
          className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground"
        />
      </button>
    </li>
  );
}

/** Label and copy for the unattributed set, shared by the strip and the drawer. */
export const UNATTRIBUTED_DETAIL = {
  key: "unattributed_mirror_invoices",
  label: "Rechnungen ohne Mietmonat-Zuordnung",
  description:
    "Diese Rechnungen haben keinen autoritativen Mietmonat im Vantio-Draft-Ledger und werden deshalb keinem Mietmonat in den Ist-Werten zugerechnet. Ein Rechnungsdatum ist kein Periodenbeweis, und eine manuelle Kontakt-Zuordnung beantwortet nicht, für welchen Monat eine Rechnung gilt.",
  family: "attributionInvoice",
};

function AttributionWarning({ attribution, onOpenDetail }) {
  const unattributed = attribution?.unattributed?.mirror_invoices;
  const n = Array.isArray(unattributed) ? unattributed.length : 0;
  if (n <= 0) return null;

  return (
    <div className="rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900 dark:border-amber-500/30 dark:bg-amber-500/10 dark:text-amber-200">
      <div className="flex items-start gap-2">
        <AlertTriangle aria-hidden="true" className="mt-0.5 h-4 w-4 shrink-0" />
        <div className="min-w-0">
          <p className="font-medium">
            {n} {n === 1 ? "Rechnung konnte" : "Rechnungen konnten"} keinem Mietmonat autoritativ
            zugeordnet werden.
          </p>
          <p className="mt-1 text-xs">
            Die Zuordnung stammt ausschliesslich aus dem Draft-Ledger. Ein Rechnungsdatum wird nicht
            als Mietmonat interpretiert, und eine manuelle Kontakt-Zuordnung beantwortet nicht, für
            welchen Monat eine Rechnung gilt.
          </p>
          <ul className="mt-2 list-none space-y-1 p-0 text-xs">
            {unattributed.slice(0, 5).map((inv) => (
              <li key={inv.bexio_invoice_id} className="flex flex-wrap items-baseline gap-x-2">
                <span className="font-medium">{inv.document_nr || `#${inv.bexio_invoice_id}`}</span>
                <span>{fmtInvoiceMoney(inv.invoiced_total, inv.currency)}</span>
                <span className="text-amber-800/80 dark:text-amber-300/80">
                  offen {fmtInvoiceMoney(inv.open_current, inv.currency, { marker: false })}
                </span>
              </li>
            ))}
          </ul>
          {/* The preview is five rows; the drawer is the whole array. The button
              is always offered, because five of five is still worth opening for
              the per-invoice amounts and statuses the strip has no room for. */}
          <button
            type="button"
            onClick={() => onOpenDetail({ ...UNATTRIBUTED_DETAIL, items: unattributed })}
            aria-label={`Alle ${n} ${
              n === 1 ? "Rechnung" : "Rechnungen"
            } ohne Mietmonat-Zuordnung anzeigen`}
            className="mt-2 rounded text-xs font-medium underline hover:no-underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            Alle {n} {n === 1 ? "Rechnung" : "Rechnungen"} anzeigen →
          </button>
        </div>
      </div>
    </div>
  );
}

function CurrencyTrust({ trust }) {
  const reliable = trust?.currency_detection_reliable === true;
  return (
    <div className="rounded-xl border border-border bg-card p-4 text-sm">
      <div className="flex items-start gap-2">
        {reliable ? (
          <Info aria-hidden="true" className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
        ) : (
          <AlertTriangle
            aria-hidden="true"
            className="mt-0.5 h-4 w-4 shrink-0 text-amber-700 dark:text-amber-300"
          />
        )}
        <div className="min-w-0">
          <p className="font-medium text-foreground">Währungserkennung</p>
          {/* Reliable is NOT complete. It says every current currency value came
              from a provider-aware write path — never that every currency is
              known, and it gates no sum anywhere. The unknown/foreign findings
              above stay visible in both states for exactly that reason. */}
          <p className="mt-1 text-xs text-muted-foreground">
            {reliable
              ? "Die Währungswerte des Rechnungsbestands stammen durchgehend aus einem providergestützten Schreibpfad. Das bedeutet nicht, dass jede Währung bekannt ist — nicht verifizierte und fremde Währungen bleiben oben separat ausgewiesen."
              : "Die Währungserkennung des Rechnungsbestands ist noch nicht vollständig verlässlich. Einzelne Werte können aus einem älteren Schreibpfad stammen."}
          </p>
          <p className="mt-1 text-xs text-muted-foreground">
            Keine Währungsumrechnung. Beträge in Fremdwährung werden nie in CHF summiert.
          </p>
        </div>
      </div>
    </div>
  );
}

/**
 * The compact version, for the overview grid — same four numbers, no detail.
 *
 * It sits where the design reference put its "Erkenntnisse" card. Four counts
 * with their populations named beside them say what that card only implied, and
 * unlike "Mietausfallquote: gut" every figure here has a source.
 */
export function RevenueDataQualitySummaryCard({ dataQuality, receivables, onOpenDetails }) {
  return (
    <div className="flex h-full flex-col rounded-xl border border-border bg-card p-4">
      <h2 className="text-sm font-semibold text-foreground">Datenqualität &amp; Hinweise</h2>
      <p className="mt-0.5 text-xs text-muted-foreground">
        Vier getrennte Grundgesamtheiten — bewusst ohne Gesamtsumme.
      </p>
      <ul className="mt-3 list-none space-y-1 p-0">
        {DATA_QUALITY_GROUPS.map((group) => {
          const count = groupFindingCount(dataQuality, group, receivables);
          return (
            <li
              key={group.id}
              className="flex items-center gap-2 border-b border-border/60 py-1.5 text-sm last:border-b-0"
            >
              <span className="min-w-0 flex-1 truncate text-foreground">{group.title}</span>
              <span
                className={`shrink-0 rounded-full border px-2 py-0.5 text-xs font-medium ${
                  count > 0
                    ? "border-amber-200 bg-amber-50 text-amber-800 dark:border-amber-500/30 dark:bg-amber-500/10 dark:text-amber-300"
                    : "border-border bg-muted text-muted-foreground"
                }`}
              >
                {count}
              </span>
            </li>
          );
        })}
      </ul>
      <button
        type="button"
        onClick={onOpenDetails}
        className="mt-3 self-start rounded text-xs font-medium text-primary hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
      >
        Details anzeigen →
      </button>
    </div>
  );
}

export default function RevenueDataQualitySection({
  dataQuality,
  attribution,
  trust,
  receivables,
}) {
  // One drawer for every finding, so the count and the copy cannot drift between
  // two of them. `null` means closed; the descriptor carries everything the
  // drawer needs, resolved here where the response is in scope.
  const [detail, setDetail] = useState(null);
  const closeDetail = useCallback((next) => {
    if (!next) setDetail(null);
  }, []);

  // The ONE finding whose records are not in the response. The hook requests them
  // only while this exact finding is open — opening the tab, or any other finding,
  // issues nothing — and forgets them again on close, so a row that reappears at
  // Bexio cannot survive here after the backend stops reporting it.
  const providerMissing = useProviderMissingInvoices(detail?.key === PROVIDER_MISSING_DETAIL_KEY);

  return (
    <div className="space-y-4">
      <AttributionWarning attribution={attribution} onOpenDetail={setDetail} />

      <div className="grid gap-4 lg:grid-cols-2">
        {DATA_QUALITY_GROUPS.map((group) => {
          const count = groupFindingCount(dataQuality, group, receivables);
          return (
            <section
              key={group.id}
              aria-label={`Datenqualität: ${group.title}`}
              className="rounded-xl border border-border bg-card p-4"
            >
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <h3 className="text-sm font-semibold text-foreground">{group.title}</h3>
                  <p className="mt-0.5 text-xs text-muted-foreground">{group.population}</p>
                </div>
                {/* A bare number badge says nothing on its own to a screen
                    reader, and "1" beside "1" is ambiguous to everyone else —
                    the label names which count this is. */}
                <span
                  aria-label={`${count} ${count === 1 ? "Hinweis" : "Hinweise"} in dieser Gruppe`}
                  className="shrink-0 rounded-full border border-border bg-muted px-2 py-0.5 text-xs font-medium text-foreground"
                >
                  {count}
                </span>
              </div>

              {count === 0 ? (
                <p className="mt-3 text-sm text-muted-foreground">Keine Hinweise in dieser Gruppe.</p>
              ) : (
                <ul className="mt-2 list-none p-0">
                  {group.items.map((item) => (
                    <FindingRow
                      key={item.key}
                      item={item}
                      source={findingSource(dataQuality, group, item, receivables)}
                      group={group}
                      onOpenDetail={setDetail}
                    />
                  ))}
                </ul>
              )}
            </section>
          );
        })}
      </div>

      <CurrencyTrust trust={trust} />

      <p className="text-xs text-muted-foreground">
        Die vier Gruppen beschreiben unterschiedliche Grundgesamtheiten und werden bewusst nicht zu
        einer Gesamtzahl addiert.
      </p>

      <RevenueQualityDetailDrawer
        open={detail != null}
        onOpenChange={closeDetail}
        detail={detail}
        remote={providerMissing}
      />
    </div>
  );
}
